import { isLeft } from "fp-ts/lib/Either";
import type { ParsingRule } from "../../types";
import { short } from "diagnostics-lib/io";
import * as io from "io-ts";

/**
 * @description Expected csv columns. We can list multiple name for a given column to ensure legacy or changes.
 * Use to check the file content.
 */
export const columnsMandatory = [
  ["name", "technicalName"],
  ["inputModelName", "boundaryConditionInputModelName"],
];
export const columnOptionals = [["orderExternalGap"], ["referenceValue"]];
export const columns = [...columnsMandatory, ...columnOptionals];

export const hasInvalidInputModelNameSize = (inputModelName: string) =>
  inputModelName && isLeft(short(io.string).decode(inputModelName));

/**
 * @description Check if file contains all required columns.
 */
export const hasInvalidColumns: ParsingRule = (header: string[]) =>
  columnsMandatory.every(columnsM => columnsM.some(col => header?.includes(col))) ? false : true;

/**
 * @description Check if file contains all required columns.
 */
export const hasInvalidFileEmpty: ParsingRule = (content: string[]) => content.length === 0;

/**
 * @description Check if file contains duplicated rows (based on name|technicalName column).
 */
export const hasDuplicatedRowByQuantityId: ParsingRule = (quantityIds: (string | undefined)[]) =>
  quantityIds.length > new Set(quantityIds).size;

/**
 * @description Check if the name column, on a given line, contain empty value.
 */
export const hasNoValueForQuantityName: ParsingRule = (quantityId: string) =>
  quantityId.length === 0;

/**
 * @description Check if the name column, on a given line, contain unexpected value.
 */
export const hasUnexpectedQuantityName: ParsingRule = (
  quantityName: string,
  expectedNameValues: string[],
) => (expectedNameValues.includes(quantityName) ? false : true);

/**
 * @description Check if the orderExternalGap value, on a given line, is a integer greater than 1.
 */
export const hasInvalidOrderExternalGapValue: ParsingRule = (orderExternalGap: string) =>
  orderExternalGap && (!Number.isInteger(Number(orderExternalGap)) || Number(orderExternalGap) < 1)
    ? true
    : false;

/*
 * @description Check if the reference value, on a given line, is a number.
 */
export const hasInvalidReferenceValue: ParsingRule = (reference: string) =>
  reference && !Number.isFinite(Number(reference)) ? true : false;

/**
 * @description Check if the orderExternalGap and reference value, on a given line, are defined by even.
 */
export const hasInvalidDefinitionExternalGap: ParsingRule = (
  orderExternalGap: string,
  reference: string,
) => (orderExternalGap && !reference ? true : false);

/**
 * @description Check if the reference value is defined without orderExternalGap, on a given line.
 */
export const hasInvalidReferenceDefinedWithoutOrderExternalGap: ParsingRule = (
  orderExternalGap: string,
  reference: string,
) => (!orderExternalGap && reference ? true : false);

/**
 * @description Check whether the 'orderExternalGap' values, across all lines, are increasing.
 */
export const hasInvalidAscendingOrderExternalGap: ParsingRule = (
  orderExternalGapValues: number[],
) => {
  const arrSorted = orderExternalGapValues.sort((a, b) => a - b);
  const startWithOne = arrSorted[0] === 1;
  if (arrSorted.length === 0) return false;
  const isAscendingOrder = arrSorted.every((value, index) => {
    const orderPrevious = arrSorted[index - 1];
    return index === 0 || (orderPrevious && value === orderPrevious + 1);
  });
  return !isAscendingOrder || !startWithOne;
};

/**
 * @description Check if the name column, on a given line, contain empty value.
 */
export const hasNoValueForModelName: ParsingRule = (modelName: string) =>
  modelName.trim().length === 0;
